import axios from 'axios';

let staticStorePromise = null;

const loadStaticStore = () => {
  if (!staticStorePromise) {
    staticStorePromise = fetch('/store-data/florashop.json').then((res) => {
      if (!res.ok) throw new Error('Static store data unavailable');
      return res.json();
    });
  }
  return staticStorePromise;
};

const stripHtml = (html = '') => html.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
const staticStoreMode = process.env.REACT_APP_STATIC_STORE_MODE === 'true';

const makeCart = (token, items = []) => {
  const subtotal = items.reduce((sum, item) => sum + item.unit_price * item.quantity, 0);
  return {
    session_token: token,
    items,
    subtotal,
    shipping: subtotal >= 75 || subtotal === 0 ? 0 : 0,
    total: subtotal,
    bundle_discount: 0,
    coupon_discount: 0,
    coupon: null,
    free_shipping: true,
  };
};

const getLocalCart = (token) => {
  const stored = localStorage.getItem(`florashop_cart_${token}`);
  return stored ? JSON.parse(stored) : makeCart(token);
};

const saveLocalCart = (cart) => {
  localStorage.setItem(`florashop_cart_${cart.session_token}`, JSON.stringify(cart));
  return cart;
};

const productCardItem = (product, variant, quantity) => ({
  item_id: `${product.handle}-${variant?.id || 'default'}-${Date.now()}`,
  product_handle: product.handle,
  variant_id: variant?.id || '',
  variant_title: variant?.title || '',
  quantity,
  unit_price: variant?.price || product.price,
  title: product.title,
  image_url: variant?.image_url || product.images?.[0]?.url || '',
  bundle_id: '',
});

const staticResponse = (config, data) => Promise.resolve({
  data,
  status: 200,
  statusText: 'OK',
  headers: {},
  config,
});

const handleStaticRequest = async (config) => {
  const method = (config.method || 'get').toLowerCase();
  const rawUrl = config.url || '';
  const parsed = new URL(rawUrl, 'https://api.florashop.us/api');
  const pathname = parsed.pathname.replace(/^\/api/, '') || '/';
  const store = await loadStaticStore();

  if (method === 'get' && pathname === '/store/config') return staticResponse(config, store.store_config);
  if (method === 'get' && pathname === '/products/categories') return staticResponse(config, store.categories);
  if (method === 'get' && pathname === '/homepage/sections') return staticResponse(config, []);
  if (method === 'get' && pathname === '/bundles') return staticResponse(config, []);

  if (method === 'get' && pathname === '/products') {
    let products = [...store.products];
    const category = parsed.searchParams.get('category');
    const search = (parsed.searchParams.get('search') || '').toLowerCase();
    const limit = Number.parseInt(parsed.searchParams.get('limit') || '12', 10);
    const page = Number.parseInt(parsed.searchParams.get('page') || '1', 10);
    if (category) products = products.filter((p) => p.category === category);
    if (search) products = products.filter((p) => `${p.title} ${stripHtml(p.description)} ${p.tags?.join(' ')}`.toLowerCase().includes(search));
    const total = products.length;
    const start = (page - 1) * limit;
    return staticResponse(config, { products: products.slice(start, start + limit), total, page, pages: Math.max(1, Math.ceil(total / limit)) });
  }

  const productMatch = pathname.match(/^\/products\/([^/]+)$/);
  if (method === 'get' && productMatch) {
    const product = store.products.find((p) => p.handle === decodeURIComponent(productMatch[1]));
    if (product) return staticResponse(config, product);
  }

  const pageMatch = pathname.match(/^\/pages\/([^/]+)$/);
  if (method === 'get' && pageMatch) {
    const page = store.pages[decodeURIComponent(pageMatch[1])];
    if (page) return staticResponse(config, page);
  }

  if (method === 'post' && pathname === '/cart') {
    const token = `local-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    return staticResponse(config, saveLocalCart(makeCart(token)));
  }

  const cartMatch = pathname.match(/^\/cart\/([^/]+)$/);
  if (method === 'get' && cartMatch) return staticResponse(config, getLocalCart(cartMatch[1]));

  const cartItemMatch = pathname.match(/^\/cart\/([^/]+)\/items$/);
  if (method === 'post' && cartItemMatch) {
    const body = JSON.parse(config.data || '{}');
    const product = store.products.find((p) => p.handle === body.product_handle);
    if (!product) throw new Error('Product not found');
    const variant = product.variants?.find((v) => v.id === body.variant_id) || product.variants?.[0] || null;
    const cart = getLocalCart(cartItemMatch[1]);
    cart.items.push(productCardItem(product, variant, body.quantity || 1));
    return staticResponse(config, saveLocalCart(makeCart(cart.session_token, cart.items)));
  }

  const cartItemUpdateMatch = pathname.match(/^\/cart\/([^/]+)\/items\/([^/]+)$/);
  if ((method === 'put' || method === 'delete') && cartItemUpdateMatch) {
    const cart = getLocalCart(cartItemUpdateMatch[1]);
    if (method === 'delete') {
      cart.items = cart.items.filter((item) => item.item_id !== cartItemUpdateMatch[2]);
    } else {
      const body = JSON.parse(config.data || '{}');
      cart.items = cart.items.map((item) => item.item_id === cartItemUpdateMatch[2] ? { ...item, quantity: body.quantity } : item);
    }
    return staticResponse(config, saveLocalCart(makeCart(cart.session_token, cart.items)));
  }

  throw new Error(`No static fallback for ${method.toUpperCase()} ${pathname}`);
};

const canHandleStaticRequest = (config) => {
  const method = (config.method || 'get').toLowerCase();
  const rawUrl = config.url || '';
  const parsed = new URL(rawUrl, 'https://api.florashop.us/api');
  const pathname = parsed.pathname.replace(/^\/api/, '') || '/';
  if (method === 'get') {
    return pathname === '/store/config'
      || pathname === '/products'
      || pathname === '/products/categories'
      || pathname === '/homepage/sections'
      || pathname === '/bundles'
      || /^\/products\/[^/]+$/.test(pathname)
      || /^\/pages\/[^/]+$/.test(pathname)
      || /^\/cart\/[^/]+$/.test(pathname);
  }
  if (method === 'post') return pathname === '/cart' || /^\/cart\/[^/]+\/items$/.test(pathname);
  if (method === 'put' || method === 'delete') return /^\/cart\/[^/]+\/items\/[^/]+$/.test(pathname);
  return false;
};

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  if (staticStoreMode && !window.location.pathname.startsWith('/admin') && canHandleStaticRequest(config)) {
    config.adapter = () => handleStaticRequest(config);
  }
  const token = localStorage.getItem('cloudstore_token');
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (!error.response && error.config && !window.location.pathname.startsWith('/admin')) {
      try {
        return await handleStaticRequest(error.config);
      } catch (fallbackError) {
        console.warn('Static store fallback failed:', fallbackError);
      }
    }
    if (error.response?.status === 401 && window.location.pathname.startsWith('/admin') && window.location.pathname !== '/admin/login') {
      localStorage.removeItem('cloudstore_token');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

export default api;
